import { useState, type ChangeEvent } from "react";
import Modal from "../../../../../../components/base/Modal";
import type { ThemeClasses, TrailerFormData } from "../types";
import type { UseFormRegister, FieldErrors, UseFormSetValue } from "react-hook-form";
import type { TFunction } from "i18next";
import Swal from "sweetalert2";
import { useDisableCRUD, useCompaniesStore } from "../../../../../../store/admin/companiesStore";
import { CRUDTooltip } from "../../../../../../components/base/CRUDTooltip";
import Select, { components, type OptionProps } from "react-select";
import { getCustomSelectStyles } from "../../../../shared";
import { useStatusesStore } from "../../../../../../store/admin/statusesStore";
import { getStatusOptionsForEntity } from "../../../../../../utils/getStatusEntity";
import { useSchemaOverrides } from "../../../../../../hooks/useSchemaOverrides";
import { brand } from "../../../../../../config/theme";

const CREATE_STATUS_VALUE = "__create_new_status__";

interface TrailerEditModalProps {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  register: UseFormRegister<TrailerFormData>;
  errors: FieldErrors<TrailerFormData>;
  loadingSubmit: boolean;
  onClose: () => void;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  onImageChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onImageDelete: () => void;
  imagePreview?: string | null;
  t: TFunction;
  setValue: UseFormSetValue<TrailerFormData>;
  watchedValues: Partial<TrailerFormData>;
  shipmentDetails: string[];
  onAddShipmentDetail: () => void;
  onRemoveShipmentDetail: (index: number) => void;
  onShipmentDetailChange: (index: number, value: string) => void;
}

export function TrailerEditModal({
  themeClasses,
  isDarkMode,
  register,
  errors,
  loadingSubmit,
  onClose,
  onSubmit,
  onImageChange,
  onImageDelete,
  imagePreview,
  t,
  setValue,
  watchedValues,
  shipmentDetails,
  onAddShipmentDetail,
  onRemoveShipmentDetail,
  onShipmentDetailChange,
}: TrailerEditModalProps) {
  const { isVisible, isRequired } = useSchemaOverrides("remolques", "putRemolque");
  const disableCRUD = useDisableCRUD();
  const [showCreateStatusModal, setShowCreateStatusModal] = useState(false);
  const [newStatusName, setNewStatusName] = useState("");
  const [creatingStatus, setCreatingStatus] = useState(false);
  const [createStatusError, setCreateStatusError] = useState<string | null>(null);

  const inputClass = `w-full px-3 py-2 ${isDarkMode ? "bg-dark-card" : "bg-gray-50"
    } border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`;

  const labelClass = `block text-sm font-medium ${themeClasses.text} mb-1`;

  // Opciones de estados desde el store
  const { statuses, createStatus, fetchStatuses } = useStatusesStore();
  // Para resellers usar la compañía seleccionada, para carriers usar su propia compañía
  const selectedCompanyId = useCompaniesStore((state) => state.companyId);
  const userCompanyId = localStorage.getItem("userCompanyId");
  const companyId = selectedCompanyId || userCompanyId;
  const baseStatusOptions = getStatusOptionsForEntity(statuses, "trailers", t, companyId);

  // Agregar opción para crear nuevo estado
  const statusOptionsSelect = [
    ...baseStatusOptions,
    { value: CREATE_STATUS_VALUE, label: `+ ${t("trailers.form.createNewStatus", { defaultValue: "Crear nuevo estado" })}` },
  ];

  const handleStatusChange = (option: any) => {
    if (option?.value === CREATE_STATUS_VALUE) {
      setShowCreateStatusModal(true);
    } else {
      setValue("status_id", option?.value || "");
    }
  };

  const handleCreateStatus = async () => {
    if (!newStatusName.trim()) {
      setCreateStatusError(t("trailers.form.statusNameRequired", { defaultValue: "El nombre del estado es requerido" }));
      return;
    }

    setCreatingStatus(true);
    setCreateStatusError(null);

    try {
      const result = await createStatus({
        name: newStatusName.trim(),
        active: true,
        company_id: companyId,
      });

      if (result.success && result.data) {
        const newStatusId = result.data.id;
        await fetchStatuses({ limit: 100, page: 1 });
        setShowCreateStatusModal(false);
        setNewStatusName("");
        // Usar setTimeout para asegurar que el estado se actualice después del re-render
        setTimeout(() => {
          setValue("status_id", newStatusId);
        }, 100);
        Swal.fire({
          title: t("trailers.form.success", { defaultValue: "Éxito" }),
          text: t("trailers.form.statusCreatedSuccess", { defaultValue: "Estado creado correctamente" }),
          icon: "success",
          confirmButtonText: t("trailers.form.understood", { defaultValue: "Entendido" }),
          confirmButtonColor: brand.primary,
        });
      } else {
        setCreateStatusError(result.error || t("trailers.form.createStatusError", { defaultValue: "Error al crear el estado" }));
      }
    } catch (err) {
      setCreateStatusError(t("trailers.form.createStatusError", { defaultValue: "Error al crear el estado" }));
    } finally {
      setCreatingStatus(false);
    }
  };

  // Custom Option component para estilizar la opción de crear
  const CustomOption = (props: OptionProps<any>) => {
    const isCreateOption = props.data.value === CREATE_STATUS_VALUE;
    return (
      <components.Option {...props}>
        <span className={isCreateOption ? "text-primary-500 font-medium" : ""}>
          {props.data.label}
        </span>
      </components.Option>
    );
  };

  // Opciones de tipo de remolque
  const typeOptions = [
    { value: "dry_van", label: t("trailers.fields.type.dry_van") },
    { value: "refrigerated", label: t("trailers.fields.type.refrigerated") },
    { value: "flatbed", label: t("trailers.fields.type.flatbed") },
    { value: "tank", label: t("trailers.fields.type.tank") },
    { value: "lowboy", label: t("trailers.fields.type.lowboy") },
    { value: "container", label: t("trailers.fields.type.container") },
    { value: "dump", label: t("trailers.fields.type.dump") },
    { value: "car_carrier", label: t("trailers.fields.type.car_carrier") },
  ];

  // Opciones de condición
  const conditionOptions = [
    { value: "excellent", label: t("trailers.fields.condition.excellent") },
    { value: "good", label: t("trailers.fields.condition.good") },
    { value: "fair", label: t("trailers.fields.condition.fair") },
    { value: "poor", label: t("trailers.fields.condition.poor") },
  ];

  // Opciones de tipo de freno
  const brakeTypeOptions = [
    { value: "air", label: t("trailers.fields.brakeType.air") },
    { value: "hydraulic", label: t("trailers.fields.brakeType.hydraulic") },
    { value: "electric", label: t("trailers.fields.brakeType.electric") },
    { value: "abs", label: t("trailers.fields.brakeType.abs") },
  ];

  // Opciones de tipo de suspensión
  const suspensionTypeOptions = [
    { value: "air", label: t("trailers.fields.suspensionType.air") },
    { value: "spring", label: t("trailers.fields.suspensionType.spring") },
    { value: "mechanical", label: t("trailers.fields.suspensionType.mechanical") },
  ];

  return (
    <Modal
      title={t("trailers.modal.editTitle")}
      onClose={onClose}
      themeClasses={themeClasses}
      width="max-w-4xl"
      content={
        <form onSubmit={onSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          {/* Imagen */}
          <div className="col-span-2 flex justify-center mb-4">
            <div className="relative w-full">
              {imagePreview ? (
                <div className="relative group">
                  <img
                    src={imagePreview}
                    alt="Vista previa"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={onImageDelete}
                    className="absolute top-2 right-2 z-10 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 transition-colors shadow-lg"
                    title={t("trailers.modal.deleteImage")}
                  >
                    <i className="ri-delete-bin-line text-xl" />
                  </button>
                  <label
                    htmlFor="edit-trailer-image"
                    className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer rounded-lg pointer-events-none"
                  >
                    <span className="text-white font-bold pointer-events-none">{t("trailers.modal.changeImage")}</span>
                  </label>
                  <input
                    id="edit-trailer-image"
                    type="file"
                    accept="image/*"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-[5]"
                    onChange={onImageChange}
                  />
                </div>
              ) : (
                <label
                  htmlFor="edit-trailer-image"
                  className="flex flex-col gap-2 py-6 items-center justify-center w-full h-full cursor-pointer border-dashed border-2 border-gray-300 rounded-lg"
                >
                  <i className="ri-image-line text-5xl text-gray-700" />
                  <span className="text-md text-gray-700 font-bold">
                    {t("trailers.modal.dragOrClickToUploadImage")}
                  </span>
                  <p className={`text-xs ${themeClasses.textSecondary}`}>
                    {t("trailers.modal.pleaseSelectAnImage")}
                  </p>
                  <input
                    id="edit-trailer-image"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={onImageChange}
                  />
                </label>
              )}
            </div>
          </div>

          {/* Información Básica */}
          <h3 className={`text-sm font-semibold ${themeClasses.text} ${themeClasses.cardBorder}`}>
            {t("trailers.sections.basicInfo")}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {isVisible("name") && (
            <div>
              <label className={labelClass}>{t("trailers.fields.name")} {isRequired("name") && <span className="text-red-500">*</span>}</label>
              <input
                {...register("name", { required: isRequired("name") ? t("trailers.validation.nameRequired") : false })}
                className={`${inputClass} ${errors.name ? "border-red-500" : ""}`}
                placeholder={t("trailers.fields.name")}
              />
              {errors.name && (
                <span className="text-red-500 text-xs">
                  {errors.name.message as string}
                </span>
              )}
            </div>
            )}
            {isVisible("type") && (
            <div>
              <label className={labelClass}>{t("trailers.typeTitle")} {isRequired("type") && <span className="text-red-500">*</span>}</label>
              <Select
                options={typeOptions}
                styles={getCustomSelectStyles(isDarkMode, !!errors.type)}
                placeholder={t("trailers.modal.selectType")}
                value={typeOptions.find((opt) => opt.value === watchedValues.type) || null}
                onChange={(option: any) => setValue("type", option?.value || "")}
                isSearchable
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              {errors.type && (
                <span className="text-red-500 text-xs">
                  {errors.type.message as string}
                </span>
              )}
            </div>
            )}
            {isVisible("brand") && (
            <div>
              <label className={labelClass}>{t("trailers.fields.brand")} {isRequired("brand") && <span className="text-red-500">*</span>}</label>
              <input
                {...register("brand", { required: isRequired("brand") ? t("trailers.validation.brandRequired") : false })}
                className={`${inputClass} ${errors.brand ? "border-red-500" : ""}`}
                placeholder="Great Dane"
              />
              {errors.brand && (
                <span className="text-red-500 text-xs">
                  {errors.brand.message as string}
                </span>
              )}
            </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {isVisible("model") && (
            <div>
              <label className={labelClass}>{t("trailers.fields.model")} {isRequired("model") && <span className="text-red-500">*</span>}</label>
              <input
                {...register("model", { required: isRequired("model") ? t("trailers.validation.modelRequired") : false })}
                className={inputClass}
                placeholder={t("trailers.fields.model")}
              />
            </div>
            )}
            {isVisible("year") && (
            <div>
              <label className={labelClass}>{t("trailers.fields.year")} {isRequired("year") && <span className="text-red-500">*</span>}</label>
              <input
                type="number"
                {...register("year", {
                  required: isRequired("year") ? t("trailers.validation.yearRequired") : false,
                  setValueAs: (v: any) =>
                    v === "" || v === null || v === undefined ? undefined : Number(v),
                })}
                className={`${inputClass} ${errors.year ? "border-red-500" : ""}`}
                placeholder="2023"
              />
              {errors.year && (
                <span className="text-red-500 text-xs">
                  {errors.year.message as string}
                </span>
              )}
            </div>
            )}
            {isVisible("plate") && (
            <div>
              <label className={labelClass}>{t("trailers.fields.plate")} {isRequired("plate") && <span className="text-red-500">*</span>}</label>
              <input
                {...register("plate", { required: isRequired("plate") ? t("trailers.validation.plateRequired") : false })}
                className={`${inputClass} ${errors.plate ? "border-red-500" : ""}`}
                placeholder="REM-001-MX"
              />
              {errors.plate && (
                <span className="text-red-500 text-xs">
                  {errors.plate.message as string}
                </span>
              )}
            </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {isVisible("vin") && (
            <div>
              <label className={labelClass}>{t("trailers.fields.vin")} {isRequired("vin") && <span className="text-red-500">*</span>}</label>
              <input
                {...register("vin", { required: isRequired("vin") ? t("trailers.validation.vinRequired") : false })}
                className={inputClass}
                placeholder="GDNE53A2023001234"
              />
            </div>
            )}
            {isVisible("serialNumber") && (
            <div>
              <label className={labelClass}>{t("trailers.fields.serialNumber")} {isRequired("serialNumber") && <span className="text-red-500">*</span>}</label>
              <input
                {...register("serialNumber", { required: isRequired("serialNumber") ? t("trailers.validation.serialNumberRequired") : false })}
                className={inputClass}
                placeholder="GD2023001"
              />
            </div>
            )}
            {isVisible("status_id") && (
            <div>
              <label className={labelClass}>{t("trailers.fields.status")} {isRequired("status_id") && <span className="text-red-500">*</span>}</label>
              <Select
                options={statusOptionsSelect}
                styles={getCustomSelectStyles(isDarkMode, !!errors.status_id)}
                placeholder={t("trailers.modal.selectStatus")}
                value={baseStatusOptions.find((opt) => opt.value === watchedValues.status_id) || null}
                onChange={handleStatusChange}
                components={{ Option: CustomOption }}
                isSearchable
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              {errors.status_id && (
                <span className="text-red-500 text-xs">
                  {errors.status_id.message as string}
                </span>
              )}
            </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {isVisible("condition") && (
            <div>
              <label className={labelClass}>{t("trailers.fields.condition")} {isRequired("condition") && <span className="text-red-500">*</span>}</label>
              <Select
                options={conditionOptions}
                styles={getCustomSelectStyles(isDarkMode, !!errors.condition)}
                placeholder={t("trailers.modal.selectCondition")}
                value={conditionOptions.find((opt) => opt.value === watchedValues.condition) || null}
                onChange={(option: any) => setValue("condition", option?.value || "")}
                isSearchable
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              {errors.condition && (
                <span className="text-red-500 text-xs">
                  {errors.condition.message as string}
                </span>
              )}
            </div>
            )}
            {isVisible("description") && (
            <div>
              <label className={labelClass}>{t("trailers.fields.description")} {isRequired("description") && <span className="text-red-500">*</span>}</label>
              <input
                {...register("description", { required: isRequired("description") ? t("trailers.validation.descriptionRequired") : false })}
                className={inputClass}
                placeholder={t("trailers.fields.description")}
              />
            </div>
            )}
          </div>

          {/* Especificaciones Técnicas */}
          <h3 className={`text-sm font-semibold ${themeClasses.text} ${themeClasses.cardBorder} mt-6`}>
            {t("trailers.sections.specifications")}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {isVisible("capacity") && (
            <div>
              <label className={labelClass}>{t("trailers.fields.capacity")} {isRequired("capacity") && <span className="text-red-500">*</span>}</label>
              <input
                {...register("capacity", { required: isRequired("capacity") ? t("trailers.validation.capacityRequired") : false })}
                className={`${inputClass} ${errors.capacity ? "border-red-500" : ""}`}
                placeholder={t("trailers.fields.capacity")}
              />
              {errors.capacity && (
                <span className="text-red-500 text-xs">
                  {errors.capacity.message as string}
                </span>
              )}
            </div>
            )}
            {isVisible("maximumWeight") && (
            <div>
              <label className={labelClass}>{t("trailers.fields.maximumWeight")} {isRequired("maximumWeight") && <span className="text-red-500">*</span>}</label>
              <input
                {...register("maximumWeight", { required: isRequired("maximumWeight") ? t("trailers.validation.maximumWeightRequired") : false })}
                className={`${inputClass} ${errors.maximumWeight ? "border-red-500" : ""}`}
                placeholder={t("trailers.fields.maximumWeight")}
              />
              {errors.maximumWeight && (
                <span className="text-red-500 text-xs">
                  {errors.maximumWeight.message as string}
                </span>
              )}
            </div>
            )}
            {isVisible("axles") && (
            <div>
              <label className={labelClass}>{t("trailers.fields.axles")} {isRequired("axles") && <span className="text-red-500">*</span>}</label>
              <input
                type="number"
                {...register("axles", {
                  required: isRequired("axles") ? t("trailers.validation.axlesRequired") : false,
                  setValueAs: (v: any) =>
                    v === "" || v === null || v === undefined ? undefined : Number(v),
                })}
                className={inputClass}
                placeholder={t("trailers.fields.axles")}
              />
            </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {isVisible("length") && (
            <div>
              <label className={labelClass}>{t("trailers.fields.length")} {isRequired("length") && <span className="text-red-500">*</span>}</label>
              <input
                {...register("length", { required: isRequired("length") ? t("trailers.validation.lengthRequired") : false })}
                className={inputClass}
                placeholder={t("trailers.fields.length")}
              />
            </div>
            )}
            {isVisible("width") && (
            <div>
              <label className={labelClass}>{t("trailers.fields.width")} {isRequired("width") && <span className="text-red-500">*</span>}</label>
              <input
                {...register("width", { required: isRequired("width") ? t("trailers.validation.widthRequired") : false })}
                className={inputClass}
                placeholder={t("trailers.fields.width")}
              />
            </div>
            )}
            {isVisible("height") && (
            <div>
              <label className={labelClass}>{t("trailers.fields.height")} {isRequired("height") && <span className="text-red-500">*</span>}</label>
              <input
                {...register("height", { required: isRequired("height") ? t("trailers.validation.heightRequired") : false })}
                className={inputClass}
                placeholder={t("trailers.fields.height")}
              />
            </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {isVisible("brakeType") && (
            <div>
              <label className={labelClass}>{t("trailers.fields.brakeType")} {isRequired("brakeType") && <span className="text-red-500">*</span>}</label>
              <Select
                options={brakeTypeOptions}
                styles={getCustomSelectStyles(isDarkMode, !!errors.brakeType)}
                placeholder={t("trailers.modal.selectBrakeType")}
                value={brakeTypeOptions.find((opt) => opt.value === watchedValues.brakeType) || null}
                onChange={(option: any) => setValue("brakeType", option?.value || "")}
                isSearchable
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              {errors.brakeType && (
                <span className="text-red-500 text-xs">
                  {errors.brakeType.message as string}
                </span>
              )}
            </div>
            )}
            {isVisible("suspensionType") && (
            <div>
              <label className={labelClass}>{t("trailers.fields.suspensionType")} {isRequired("suspensionType") && <span className="text-red-500">*</span>}</label>
              <Select
                options={suspensionTypeOptions}
                styles={getCustomSelectStyles(isDarkMode, !!errors.suspensionType)}
                placeholder={t("trailers.modal.selectSuspensionType")}
                value={suspensionTypeOptions.find((opt) => opt.value === watchedValues.suspensionType) || null}
                onChange={(option: any) => setValue("suspensionType", option?.value || "")}
                isSearchable
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              {errors.suspensionType && (
                <span className="text-red-500 text-xs">
                  {errors.suspensionType.message as string}
                </span>
              )}
            </div>
            )}
          </div>

          {/* Documentación */}
          <h3 className={`text-sm font-semibold ${themeClasses.text} ${themeClasses.cardBorder} mt-6`}>
            {t("trailers.sections.documents")}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {isVisible("registration") && (
            <div>
              <label className={labelClass}>{t("trailers.fields.registration")} {isRequired("registration") && <span className="text-red-500">*</span>}</label>
              <input
                {...register("registration", { required: isRequired("registration") ? t("trailers.validation.registrationRequired") : false })}
                className={inputClass}
                placeholder="REG-001-2024"
              />
            </div>
            )}
            {isVisible("insurance") && (
            <div>
              <label className={labelClass}>{t("trailers.fields.insurance")} {isRequired("insurance") && <span className="text-red-500">*</span>}</label>
              <input
                {...register("insurance", { required: isRequired("insurance") ? t("trailers.validation.insuranceRequired") : false })}
                className={inputClass}
                placeholder="INS-001-2024"
              />
            </div>
            )}
            {isVisible("permit") && (
            <div>
              <label className={labelClass}>{t("trailers.fields.permit")} {isRequired("permit") && <span className="text-red-500">*</span>}</label>
              <input
                {...register("permit", { required: isRequired("permit") ? t("trailers.validation.permitRequired") : false })}
                className={inputClass}
                placeholder="PER-001-2024"
              />
            </div>
            )}
          </div>

          {/* Detalles de Viaje */}
          <h3 className={`text-sm font-semibold ${themeClasses.text} ${themeClasses.cardBorder} mt-6`}>
            {t("trailers.sections.shipmentDetails")}
          </h3>

          <div className="space-y-2">
            {shipmentDetails.length === 0 ? (
              <p className={`text-sm ${themeClasses.textSecondary}`}>
                {t("trailers.modal.noShipmentDetails")}
              </p>
            ) : (
              shipmentDetails.map((detail, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={detail}
                    onChange={(e) => onShipmentDetailChange(index, e.target.value)}
                    className={inputClass}
                    placeholder={t("trailers.modal.shipmentDetailPlaceholder")}
                  />
                  <button
                    type="button"
                    onClick={() => onRemoveShipmentDetail(index)}
                    className="px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              ))
            )}
            <button
              type="button"
              onClick={onAddShipmentDetail}
              className={`px-4 py-2 ${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} hover:bg-primary-500 hover:text-gray-900 transition-colors flex items-center gap-2`}
            >
              <i className="ri-add-line"></i>
              {t("trailers.actions.addShipmentDetail")}
            </button>
          </div>

          {/* Campos para futuras implementaciones */}
          {/*
          <h3 className={`text-sm font-semibold ${themeClasses.text} ${themeClasses.cardBorder} mt-6`}>
            {t("trailers.sections.vehicleAssociation")}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>{t("trailers.fields.attached")}</label>
              <input type="checkbox" {...register("attached")} />
            </div>
            <div>
              <label className={labelClass}>{t("trailers.fields.vehicle")}</label>
              <Select
                options={vehicleOptions}
                styles={getCustomSelectStyles(isDarkMode)}
                placeholder={t("trailers.modal.selectVehicle")}
                value={vehicleOptions.find((opt) => opt.value === watchedValues.vehicle_id) || null}
                onChange={(option: any) => setValue("vehicle_id", option?.value || "")}
                isSearchable
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
          </div>
          */}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} font-medium transition-colors`}
            >
              {t("trailers.actions.cancel")}
            </button>
            <CRUDTooltip>
              <button
                type="submit"
                disabled={loadingSubmit || disableCRUD}
                className="px-4 py-2 bg-primary-500 hover:bg-primary-400 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {loadingSubmit ? t("trailers.actions.saving") : t("trailers.actions.save")}
              </button>
            </CRUDTooltip>
          </div>

          {/* Modal para crear estado personalizado */}
          {showCreateStatusModal && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
              <div className={`${isDarkMode ? "bg-dark-bg" : "bg-white"} rounded-xl max-w-md w-full overflow-hidden border ${themeClasses.cardBorder} shadow-2xl`}>
                <div className={`flex items-center justify-between p-4 border-b ${themeClasses.cardBorder}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
                      <i className="ri-add-line text-black text-lg"></i>
                    </div>
                    <h2 className={`font-semibold text-lg ${themeClasses.text}`}>
                      {t("trailers.form.createCustomStatus", { defaultValue: "Crear Estado Personalizado" })}
                    </h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateStatusModal(false);
                      setNewStatusName("");
                      setCreateStatusError(null);
                    }}
                    className={`p-2 ${themeClasses.buttonHover} rounded-lg transition-colors`}
                  >
                    <i className={`ri-close-line text-xl ${themeClasses.text}`}></i>
                  </button>
                </div>

                <div className="p-6">
                  <div className="mb-4">
                    <label className={`block text-sm ${themeClasses.text} mb-2`}>
                      {t("trailers.form.statusName", { defaultValue: "Nombre del estado" })} *
                    </label>
                    <input
                      type="text"
                      value={newStatusName}
                      onChange={(e) => setNewStatusName(e.target.value)}
                      placeholder={t("trailers.form.statusNamePlaceholder", { defaultValue: "Ej: En reparación" })}
                      className={inputClass}
                      disabled={creatingStatus}
                    />
                  </div>

                  <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-4`}>
                    {t("trailers.form.customStatusInfo", { defaultValue: "Este estado personalizado estará disponible solo para tu compañía." })}
                  </p>

                  {createStatusError && (
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 mb-4">
                      <div className="flex items-start gap-2">
                        <i className="ri-error-warning-line text-red-500 mt-0.5"></i>
                        <p className="text-sm text-red-500">{createStatusError}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateStatusModal(false);
                        setNewStatusName("");
                        setCreateStatusError(null);
                      }}
                      disabled={creatingStatus}
                      className={`px-6 py-2.5 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.text} ${themeClasses.buttonHover} transition-colors font-medium`}
                    >
                      {t("trailers.actions.cancel")}
                    </button>
                    <button
                      type="button"
                      onClick={handleCreateStatus}
                      disabled={!newStatusName.trim() || creatingStatus}
                      className="px-6 py-2.5 rounded-lg bg-primary-500 text-black font-medium hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {creatingStatus && <i className="ri-refresh-line animate-spin"></i>}
                      {creatingStatus ? t("trailers.actions.saving", { defaultValue: "Creando..." }) : t("trailers.actions.create", { defaultValue: "Crear" })}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </form>
      }
    />
  );
}
